import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CircularProgress, Icon } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import SoftPagination from "components/SoftPagination";
import SoftInput from "components/SoftInput";
import DataTable from "./DataTable/index";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";

const RolesList = () => {
  const { permissions: ctxPerms = [], isAuthReady } = useAuth();
  const navigate = useNavigate();

  const storedPerms = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return (localStorage.getItem("permissions") || "").split(",").filter(Boolean);
    }
  }, []);

  const permSet = useMemo(
    () => new Set((ctxPerms.length ? ctxPerms : storedPerms).map((p) => (typeof p === "string" ? p : p.name))),
    [ctxPerms, storedPerms]
  );
  const userHas = useCallback((colKey) => permSet.has(`viewroles.${colKey}`), [permSet]);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPag] = useState({ page: 1, perPage: 25, total: 0 });
  const [pageInput, setPageInput] = useState(1);
  const [filters, setFilters] = useState({ q: "", actor_type: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const roleQueryString = useCallback(
    (includePagination = true) => {
      const params = new URLSearchParams();
      if (includePagination) {
        params.set("page", pagination.page);
        params.set("per_page", pagination.perPage);
      }
      params.set("hierarchy_scope", "1");
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, value);
        }
      });
      return params.toString();
    },
    [filters, pagination.page, pagination.perPage]
  );

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token missing");

      const res = await fetch(`${API_URL}/access-roles?${roleQueryString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP error - ${res.status}`);

      const pageData = data.roles?.data || data.roles || data.data || [];
      const total = data.roles?.total ?? data.total ?? pageData.length;

      setRoles(Array.isArray(pageData) ? pageData : []);
      setPag((p) => ({ ...p, total }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Failed to fetch roles", color: "error" });
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [roleQueryString]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    setPageInput(pagination.page);
  }, [pagination.page]);

  const allColumns = [
    { key: "id", label: "id", align: "left" },
    { key: "name", label: "name", align: "left" },
    { key: "actor_type", label: "type", align: "left" },
    { key: "user_scope", label: "User Access", align: "left" },
    { key: "parent", label: "parent", align: "left" },
    { key: "permissions", label: "Perms", align: "center" },
    { key: "scopes", label: "Scopes", align: "center" },
    { key: "members", label: "Users", align: "center" },
    { key: "action", label: "Action", align: "center" },
  ];

  const hasColumnPermissionConfig = useMemo(
    () => allColumns.some((col) => permSet.has(`viewroles.${col.key}`)),
    [allColumns, permSet]
  );

  const visibleColumns = useMemo(
    () => (hasColumnPermissionConfig ? allColumns.filter((col) => userHas(col.key)) : allColumns),
    [allColumns, hasColumnPermissionConfig, userHas]
  );

  const rows = roles.map((role) => {
    const base = {
      id: role.id,
      name: role.name,
      actor_type: role.actor_type,
      user_scope: role.user_scope === "all" ? "All Users" : "Self",
      parent: role.parent?.name || "-",
      permissions: role.permissions_count ?? role.permissions?.length ?? 0,
      scopes: role.scopes_count ?? role.scopes?.length ?? 0,
      members: role.members_count ?? role.members?.length ?? 0,
      action: (
        <SoftBox display="flex" justifyContent="center">
          <SoftButton size="small" color="info" onClick={() => navigate(`/roles/view/${role.id}`)}>
            Permissions
          </SoftButton>
        </SoftBox>
      ),
    };

    return hasColumnPermissionConfig
      ? Object.fromEntries(Object.entries(base).filter(([key]) => userHas(key)))
      : base;
  });

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.perPage));
  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPag((prev) => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/access-roles/export?${roleQueryString(false)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Export failed");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "access-roles.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Export failed", color: "error" });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPag((p) => ({ ...p, page: newPage }));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Role"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((s) => ({ ...s, open: false }))}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox mb={3}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <SoftTypography variant="h5" fontWeight="medium">
              Roles
            </SoftTypography>
            <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              <SoftButton size="small" color="dark" variant="outlined" startIcon={<Icon>download</Icon>} onClick={handleExport}>
                Export
              </SoftButton>
              <SoftButton size="small" color="info" onClick={() => navigate("/roles/add")}>
                <SoftTypography variant="button" color="white">
                  Create Role
                </SoftTypography>
              </SoftButton>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <SoftBox width={{ xs: "100%", sm: "220px" }}>
              <SoftInput
                size="small"
                placeholder="Search roles"
                value={filters.q}
                onChange={handleFilterChange("q")}
              />
            </SoftBox>
            <select
              value={filters.actor_type}
              onChange={handleFilterChange("actor_type")}
              style={{ minHeight: 38, minWidth: 160, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
            >
              <option value="">All Types</option>
              <option value="internal">Internal</option>
              <option value="client">Client</option>
              <option value="vendor">Vendor</option>
              <option value="all">All</option>
            </select>
          </SoftBox>
        </SoftBox>

        {loading || !isAuthReady ? (
          <SoftBox textAlign="center" py={4}>
            <CircularProgress />
          </SoftBox>
        ) : roles.length && visibleColumns.length ? (
          <Card>
            <SoftBox display="flex" justifyContent="center" alignItems="center" my={2} gap={1}>
              <SoftBox fontWeight="small" style={{ width: "100%", fontSize: "12px" }}>
                Showing {(pagination.page - 1) * pagination.perPage + 1} to{" "}
                {Math.min(pagination.page * pagination.perPage, pagination.total)} of {pagination.total} entries
              </SoftBox>

              <SoftBox display="flex" justifyContent="end" alignItems="center" p={1} style={{ width: "100%", fontSize: "12px" }}>
                <SoftPagination item onClick={() => handlePageChange(1)} disabled={pagination.page === 1}>
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>first_page</Icon>
                </SoftPagination>
                <SoftPagination item onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1}>
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>chevron_left</Icon>
                </SoftPagination>

                <span style={{ margin: "0 5px", width: "120px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px" }}>
                  <span width="5rem" mx={1}>
                    <SoftInput
                      inputProps={{ type: "number", min: 1, max: totalPages, style: { fontSize: "12px" } }}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handlePageChange(parseInt(pageInput, 10))}
                      onBlur={() => handlePageChange(parseInt(pageInput, 10))}
                      sx={{ fontSize: "12px", height: "28px" }}
                    />
                  </span>
                  /<span>{totalPages}</span>
                </span>

                <SoftPagination item onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === totalPages}>
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>chevron_right</Icon>
                </SoftPagination>
                <SoftPagination item onClick={() => handlePageChange(totalPages)} disabled={pagination.page === totalPages}>
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>last_page</Icon>
                </SoftPagination>
              </SoftBox>
            </SoftBox>

            <DataTable
              table={{
                columns: visibleColumns.map(({ key, label, align }) => ({
                  Header: label,
                  accessor: key,
                  align,
                })),
                rows,
              }}
            />
          </Card>
        ) : (
          <SoftBox textAlign="center" py={4}>
            <SoftTypography variant="body2">
              {visibleColumns.length === 0 ? "You do not have permission to view any columns." : "No roles found"}
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>
    </DashboardLayout>
  );
};

export default RolesList;
